import {Stack} from 'src/design/atoms/Stack';
import React, {useState} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {isValidArray} from 'src/utilities/objectUtils';

import {useDisclosure} from '@mantine/hooks';
import {
  AttachmentModuleEnum,
  EntityIdentifierEnum,
  FeatureFlagEnum,
  ModuleEnum,
  NoteModuleEnum,
  NotificationModuleEnum,
  TaskDefinitionStateEnum,
  TaskTypeEnum,
} from 'src/api/ProactApiClient';
import {useDeleteTaskDefinitionMutation} from 'src/redux/queries/taskDefinition';
import {BackBar} from 'src/app/layout/BackBar';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {SidebarLinkActions} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {SplitPane} from 'src/design/atoms/SplitPane';
import {ActionContents} from 'src/app/actions/ActionContents';
import {ActionsDocumentTitle} from 'src/app/actions/actionHelpers';
import {useActionView} from 'src/app/actions/useActionView';
import {AttachmentsModule} from 'src/app/attachments/AttachmentsModule';
import {AuditModule} from 'src/app/audit-logs/AuditModule';
import {LockGuard} from 'src/app/lock-mgt/LockGuard';
import {NotesModule} from 'src/app/notes/NotesModule';
import {NotificationsModule} from 'src/app/notifications/NotificationsModule';
import {AdminTaskAssignmentsListByTask} from 'src/app/tasks/AdminTaskAssignmentsListByTask';
import {TasksPendingSyncBanner} from 'src/app/tasks/offline/TasksPendingSyncBanner';
import {TasksOfflineBoundary} from 'src/app/tasks/TasksOfflineBoundary';
import {TaskStateBanner} from 'src/app/tasks/TaskStateBanner';
import {taskRowSubText} from 'src/app/tasks/taskHelpers';
import {ViewLinkedWorkflowSection} from 'src/app/workflows/ViewLinkedWorkflowSection';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAuditLogPermissions} from 'src/shared/auth/permissions/useAuditLogPermissions';
import {useExtensionTabs} from 'src/shared/hooks/useExtensionTabs';
import {useUrlQueryTabV2} from 'src/shared/hooks/useUrlQueryTabV2';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useActionsBreadcrumbs} from '../hooks/useActionsBreadcrumbs';
import {useMyActionsBreadcrumbs} from '../hooks/useMyActionsBreadcrumbs';
import ActionDetails from './components/ActionDetails';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';
type ViewType =
  | 'content'
  | 'details'
  | 'assign'
  | 'notes'
  | 'notifications'
  | 'attachments'
  | 'audit'
  | '';

const ViewActionPage: React.FC = () => {
  useSetDocumentTitle(ActionsDocumentTitle);
  const featureFlag = FeatureFlagEnum.Actions;
  const taskType = TaskTypeEnum.Actions;
  const module = ModuleEnum.Actions;

  const auditPerms = useAuditLogPermissions();

  const navigate = useAppNavigate();
  const location = useLocation();

  // Determine if we're on the user path (My Actions) vs manage path
  const isUserPath = location.pathname.includes('/actions/user/');

  const {setAlert} = useAlert();

  const [confirmDeleteOpened, confirmDeleteHandlers] = useDisclosure(false);
  const [, setIsDeleting] = useState(false);

  const [deleteTaskDefinition] = useDeleteTaskDefinitionMutation();

  const {taskId, view} = useParams();

  const parsedTaskId =
    taskId !== undefined && !isNaN(+taskId) ? +taskId : undefined;

  const {
    viewItem,
    viewAssignment,
    isLoading,
    refetch: getData,
    formMeta,
    formData,
    pills,
  } = useActionView(parsedTaskId);

  const extensionTabs = useExtensionTabs(module, parsedTaskId ?? 0);

  const onConfirmDelete = async () => {
    confirmDeleteHandlers.close();

    try {
      if (!taskId) {
        setAlert('ID not found.', 'error');
        return;
      }

      setIsDeleting(true);

      await deleteTaskDefinition(+taskId!).unwrap();

      setAlert(
        'Item was successfully deleted. This change may take a few minutes to appear.',
        'success'
      );

      navigate(PathUtility.TasksDashboard(taskType, 'manage'));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDeleting(false);
  };

  // Use appropriate breadcrumbs based on the path
  const {backBarProps: manageBackBarProps} = useActionsBreadcrumbs({
    mode: 'view',
    item: viewItem,
  });
  const {backBarProps: userBackBarProps} = useMyActionsBreadcrumbs({
    item: viewItem,
  });
  const backBarProps = isUserPath ? userBackBarProps : manageBackBarProps;

  const handleClone = () => navigate(PathUtility.ActionClone(taskId as string));
  const handleEdit = () => navigate(PathUtility.ActionEdit(taskId as string));
  const handleDelete = () => confirmDeleteHandlers.open();

  const canEdit =
    viewItem?.state === TaskDefinitionStateEnum.Draft &&
    viewItem?.locked !== true;
  const canDelete = viewItem?.locked !== true;

  const {RenderedTabsComponent} = useUrlQueryTabV2<ViewType>({
    view: (view ?? '') as ViewType,
    defaultView: 'content',
    tabs: [
      {
        view: 'content',
        label: 'Content',
        component: (
          <SplitPane
            pane={
              <ViewLinkedWorkflowSection taskDefinitionId={viewItem?.id} />
            }>
            <ActionContents
              module={module}
              formData={formData}
              formMeta={formMeta}
              item={viewItem}
              assignment={viewAssignment}
              onRefreshTransition={getData}
              onOfflineQueued={() =>
                navigate(PathUtility.TasksDashboard(taskType, 'user'))
              }
            />
          </SplitPane>
        ),
      },
      {
        view: 'assign',
        label: 'Assign',
        component: viewItem && (
          <AdminTaskAssignmentsListByTask
            item={viewItem}
            onTransition={getData}
            module={module}
          />
        ),
      },
      {
        view: 'notes',
        label: 'Notes',
        component: (
          <>
            <NotesModule
              entityId={viewItem?.id}
              noteType={NoteModuleEnum.TaskDefinition}
            />
          </>
        ),
      },
      {
        view: 'attachments',
        label: 'Attachments',
        component: (
          <>
            {viewItem?.id && (
              <AttachmentsModule
                entityId={viewItem.id}
                attachmentType={AttachmentModuleEnum.TaskDefinition}
              />
            )}
          </>
        ),
      },
      {
        view: 'notifications',
        label: 'Notifications',
        component: (
          <>
            <NotificationsModule
              entityId={viewItem?.id}
              notificationType={NotificationModuleEnum.TaskDefinition}
            />
          </>
        ),
      },
      {
        view: 'audit',
        label: 'Audit',
        disabled: !auditPerms.view,
        component: viewItem?.id && (
          <AuditModule
            entityId={String(viewItem.id)}
            entityType={EntityIdentifierEnum.TaskDefinition}
          />
        ),
      },
      {
        view: 'details',
        label: 'Details',
        component: (
          <>
            <ActionDetails item={viewItem} />
          </>
        ),
      },
      ...extensionTabs,
    ],
    onSelectView: view =>
      navigate(
        isUserPath
          ? PathUtility.ActionViewUser(taskId as string, view as any)
          : PathUtility.ActionView(taskId as string, view as any)
      ),
  });

  const subText = viewItem ? taskRowSubText(viewItem) : '';

  return (
    <TasksOfflineBoundary>
    <ContentWrapper featureFlag={featureFlag}>
      {!isLoading && viewItem ? (
        <Stack gap="md" w="100%">
          <BackBar {...backBarProps} />

          <PageHeader>
            <PageHeader.Title icon={SidebarLinkActions.icon}>
              {viewItem.subject}
            </PageHeader.Title>

            <PageHeader.Badges>
              {pills.map((pill, index) => (
                <PageHeader.Badge
                  key={index}
                  color={pill.color}
                  variant="light">
                  {pill.text}
                </PageHeader.Badge>
              ))}
            </PageHeader.Badges>

            {subText && <PageHeader.Meta>{subText}</PageHeader.Meta>}

            <PageHeader.Actions>
              <PageHeader.Action variant="clone" onClick={handleClone}>
                Clone
              </PageHeader.Action>
              {canEdit && (
                <PageHeader.Action variant="edit" onClick={handleEdit}>
                  Edit
                </PageHeader.Action>
              )}
              {canDelete && (
                <PageHeader.Action variant="delete" onClick={handleDelete}>
                  Delete
                </PageHeader.Action>
              )}
            </PageHeader.Actions>
          </PageHeader>

          <TasksPendingSyncBanner />

          <TaskStateBanner module={module} state={viewItem.state} />

          <LockGuard
            id={viewItem.id}
            type={EntityIdentifierEnum.TaskDefinition}
            lockCache={viewItem.locked}
            onUnlock={getData}
          />

          {RenderedTabsComponent}

          <ConfirmDialog
            opened={confirmDeleteOpened}
            onClose={() => confirmDeleteHandlers.close()}
            onConfirm={onConfirmDelete}
            confirmationButtonColor="red"
            title="Are you sure you want to delete this task?"
            body="This action cannot be undone. The task will be deleted, including related assignment data and statistics."
          />
        </Stack>
      ) : (
        <TableBodyRender
          isLoading={isLoading}
          hasData={isValidArray(formData)}
        />
      )}
    </ContentWrapper>
    </TasksOfflineBoundary>
  );
};

// Register the manage path route (from Manage Actions tab)
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ActionView(':taskId', ':view'),
  element: <ViewActionPage />,
  isPrivate: true,
  supportsOffline: true,
});

// Register the user path route (from My Actions tab)
routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.ActionViewUser(':taskId', ':view'),
  element: <ViewActionPage />,
  isPrivate: true,
  supportsOffline: true,
});

export default ViewActionPage;
